import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadRulesService, LeadRule, LeadRuleCreate } from '../../services/leadRules.service'
import RuleModal from './RuleModal'
import toast from 'react-hot-toast'

interface RulesPanelProps {
  selectedRuleId?: number
  onSelectRule: (ruleId: number | undefined) => void
}

export default function RulesPanel({ selectedRuleId, onSelectRule }: RulesPanelProps) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingRule, setEditingRule] = useState<LeadRule | null>(null)

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['leadRules'],
    queryFn: () => leadRulesService.getRules(),
  })

  const createMutation = useMutation({
    mutationFn: (data: LeadRuleCreate) => leadRulesService.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadRules'] })
      toast.success('Правило создано')
      setShowModal(false)
    },
    onError: () => toast.error('Ошибка при создании правила'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: LeadRuleCreate }) =>
      leadRulesService.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadRules'] })
      toast.success('Правило обновлено')
      setEditingRule(null)
    },
    onError: () => toast.error('Ошибка при обновлении'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => leadRulesService.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadRules'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success('Правило удалено')
      if (selectedRuleId) onSelectRule(undefined)
    },
    onError: () => toast.error('Ошибка при удалении'),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      leadRulesService.updateRule(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadRules'] })
      toast.success('Статус обновлён')
    },
  })

  const handleSave = (data: LeadRuleCreate) => {
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleDelete = (rule: LeadRule) => {
    if (confirm(`Удалить правило "${rule.name}"? Найденные лиды сохранятся.`)) {
      deleteMutation.mutate(rule.id)
    }
  }

  if (isLoading) {
    return (
      <div className="card mb-6">
        <div className="animate-pulse flex gap-4">
          <div className="h-8 bg-gray-200 rounded w-32"></div>
          <div className="h-8 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
          Правила поиска
        </h3>
        <button
          onClick={() => { setEditingRule(null); setShowModal(true) }}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          + Новое правило
        </button>
      </div>

      {/* Rules chips */}
      {rules.length === 0 ? (
        <div className="card text-center py-6 text-gray-500 text-sm">
          Нет правил. Создайте правило, чтобы AI начал находить лидов при парсинге.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {/* All leads chip */}
          <button
            onClick={() => onSelectRule(undefined)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !selectedRuleId
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Все лиды
          </button>

          {rules.map((rule) => (
            <div key={rule.id} className="group relative">
              <button
                onClick={() => onSelectRule(selectedRuleId === rule.id ? undefined : rule.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  selectedRuleId === rule.id
                    ? 'bg-blue-600 text-white'
                    : rule.is_active
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                }`}
              >
                {!rule.is_active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"></span>
                )}
                {rule.name}
                <span className={`text-xs ${
                  selectedRuleId === rule.id ? 'text-blue-200' : 'text-gray-400'
                }`}>
                  {rule.leads_count}
                </span>
              </button>

              {/* Dropdown on hover */}
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 min-w-[140px]">
                <button
                  onClick={() => { setEditingRule(rule); setShowModal(true) }}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                >
                  Редактировать
                </button>
                <button
                  onClick={() => toggleActive.mutate({ id: rule.id, is_active: !rule.is_active })}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {rule.is_active ? 'Выключить' : 'Включить'}
                </button>
                <button
                  onClick={() => handleDelete(rule)}
                  className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <RuleModal
          rule={editingRule}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingRule(null) }}
          saving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}
